import { MapPin, Home, TrendingUp, Users } from "lucide-react";

const ServiceAreas = () => {
  const areas = [
    {
      name: "Toronto",
      description: "Canada's largest city offers diverse neighborhoods from downtown condos to family-friendly suburbs. Expert knowledge of all districts including The Annex, Yorkville, and The Beaches.",
      highlights: ["Downtown Core", "Yorkville", "The Annex", "The Beaches", "Liberty Village"],
    },
    {
      name: "North York",
      description: "Dynamic area with excellent schools, shopping centers, and a mix of high-rise condos and detached homes. Specializing in Willowdale, Bayview Village, and Don Mills.",
      highlights: ["Willowdale", "Bayview Village", "Don Mills", "Yonge & Sheppard", "York Mills"],
    },
    {
      name: "Scarborough",
      description: "Diverse community offering excellent value with growing infrastructure and development. Expertise in The Bluffs, Agincourt, and Rouge Park areas.",
      highlights: ["The Bluffs", "Agincourt", "Rouge Park", "Malvern", "Guildwood"],
    },
    {
      name: "Etobicoke",
      description: "Western Toronto neighborhoods featuring waterfront properties, parks, and established communities. Specializing in Mimico, Humber Bay, and The Kingsway.",
      highlights: ["Mimico", "Humber Bay", "The Kingsway", "Long Branch", "Islington"],
    },
    {
      name: "Mississauga",
      description: "Canada's 6th largest city with modern developments, excellent transit, and diverse housing options. Expert in Port Credit, Streetsville, and Square One area.",
      highlights: ["Port Credit", "Streetsville", "Square One", "Lorne Park", "Cooksville"],
    },
    {
      name: "Markham",
      description: "York Region's technology hub with top-rated schools and new developments. Specializing in Unionville, Cornell, and Markham Village.",
      highlights: ["Unionville", "Cornell", "Markham Village", "Thornhill", "Berczy Village"],
    },
    {
      name: "Richmond Hill",
      description: "Upscale community with executive homes, excellent schools, and beautiful parks. Expertise in Oak Ridges, Bayview Hill, and Westbrook.",
      highlights: ["Oak Ridges", "Bayview Hill", "Westbrook", "Langstaff", "Elgin Mills"],
    },
    {
      name: "Vaughan",
      description: "Fast-growing city with modern infrastructure and diverse housing. Specializing in Maple, Woodbridge, and Kleinburg.",
      highlights: ["Maple", "Woodbridge", "Kleinburg", "Thornhill", "Concord"],
    },
    {
      name: "Brampton",
      description: "Peel Region's largest city offering excellent value and new developments. Expert knowledge of Bramalea, Churchville, and Mount Pleasant.",
      highlights: ["Bramalea", "Churchville", "Mount Pleasant", "Heart Lake", "Castlemore"],
    },
  ];

  return (
    <div data-testid="service-areas-page">
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">Service Areas</h1>
          <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto leading-relaxed">
            Comprehensive coverage across the Greater Toronto Area
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Greater Toronto Area</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Where I Work
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              With over 15 years of experience in the GTA real estate market, I have intimate knowledge of neighborhoods across Toronto and surrounding communities. My expertise ensures you receive valuable insights whether buying or selling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {areas.map((area, index) => (
              <div key={index} className="bg-white border border-border p-8 hover:shadow-lg transition-shadow" data-testid={`area-${area.name.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-accent/10 flex items-center justify-center mr-4">
                    <MapPin className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="text-2xl font-heading font-semibold text-primary">{area.name}</h3>
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed">{area.description}</p>
                <div>
                  <p className="text-sm uppercase tracking-widest font-semibold text-accent mb-3">Key Areas:</p>
                  <div className="flex flex-wrap gap-2">
                    {area.highlights.map((highlight, idx) => (
                      <span key={idx} className="text-xs bg-secondary px-3 py-1 text-muted-foreground">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Market Expertise</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Why Local Knowledge Matters
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Home className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Neighborhood Insights</h3>
              <p className="text-muted-foreground leading-relaxed">
                Deep understanding of schools, amenities, transportation, and community character in every area I serve.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <TrendingUp className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Market Trends</h3>
              <p className="text-muted-foreground leading-relaxed">
                Real-time knowledge of pricing trends, inventory levels, and market conditions specific to each neighborhood.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Local Connections</h3>
              <p className="text-muted-foreground leading-relaxed">
                Established network of professionals including inspectors, contractors, and lenders throughout the GTA.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section 
        className="relative py-20 md:py-32 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1666490971707-d499de4c532b?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-6">
            Looking in a Specific Area?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
            Let me provide you with detailed information about any GTA neighborhood
          </p>
          <a href="/contact">
            <button className="bg-white text-primary hover:bg-white/90 h-14 px-10 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300" data-testid="contact-cta-button">
              Contact Me Today
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreas;